/**
 * sentry管理
 */

import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import * as Tracing from '@sentry/tracing';
import history from '../../app/history';

const sentryInstances = new Map<string, Sentry.Hub>();

window.Sentry = Sentry;

interface InitMicroAppSentryInstanceConfig {
  dsn: string;
  appname: string;
  release?: string;
}

function shouldFilterError(hint: any) {
  // 网络中断不上报
  if (hint && hint.originalException && hint.originalException.__CANCEL__) {
    return true;
  }

  // 网络异常不上报
  if (
    hint &&
    hint.originalException &&
    (hint.originalException.name === 'ApiBusinessError' ||
      hint.originalException.name === 'ApiNetError')
  ) {
    return true;
  }

  return false;
}

export { Tracing };

/**
 * 初始化基座sentry
 * @param config {InitMicroAppSentryInstanceConfig} 配置
 * @returns {Sentry.Hub}
 */
export function initBaseInstance(
  config: Omit<InitMicroAppSentryInstanceConfig, 'appname'>
) {
  if (process.env.NODE_ENV === 'development') return;

  // 这个实例不会上报任何异常，仅仅作为各个应用的中转站
  Sentry.init({
    dsn: config.dsn,
    release: config.release,
    environment: process.env.MED_APP_ENV,
    integrations: [
      new TracingIntegrations.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
      }),
    ],
    tracesSampleRate: 0.2,
    beforeSend(event: any, hint: any) {
      if (shouldFilterError(hint)) {
        return null;
      }

      if (hint && hint.originalException && hint.originalException.stack) {
        const { originalException } = hint;
        const stacks = originalException.stack.split(/\n/).slice(1);
        const matched = (stacks[0] || '').match(/at\s+(.+js):\d+:\d+/);
        const fileUrl = matched && matched[1];

        if (fileUrl) {
          const matched = fileUrl.match(/patient-micro-(.+)\//i);

          if (matched) {
            const appname = matched[1];

            const instance = getMicroAppSentryInstance(appname);

            if (instance) {
              instance.getClient()?.captureEvent(event, hint);

              return false;
            }
          }
        }
      }

      return event;
    },
  });

  sentryInstances.set('base', Sentry.getCurrentHub());
}

/**
 * 初始化微应用的sentry配置
 * @param config {InitMicroAppSentryInstanceConfig} 配置
 * @returns {Sentry.Hub}
 */
export function initMicroAppSentryInstance(
  config: InitMicroAppSentryInstanceConfig
) {
  if (process.env.NODE_ENV === 'development') return;

  const { dsn, appname } = config;

  if (!dsn || sentryInstances.get(appname)) return;

  // @ts-ignore
  const currentRelease = window.SENTRY_RELEASE && window.SENTRY_RELEASE.id;

  const client = new Sentry.BrowserClient({
    dsn,
    environment: process.env.MED_APP_ENV,
    defaultIntegrations: Sentry.defaultIntegrations,
    beforeSend: function (event: any, hint: any) {
      // 修正release
      event.release = currentRelease;

      if (shouldFilterError(hint)) {
        return null;
      }

      return event;
    },
  });

  const hub = new Sentry.Hub(client);

  sentryInstances.set(appname, hub);

  return hub;
}

/**
 * 获取sentry实例
 * @param appname {string} 应用名
 * @returns {Sentry.Hub}
 */
export const getMicroAppSentryInstance = (appname: string) => {
  const instance = sentryInstances.get(appname);

  return instance;
};
